import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { Tooltip as MuiTooltip } from '@mui/material'
import { format } from 'date-fns'

import MailIcon from '@mui/icons-material/Mail'
import CreditCardIcon from '@mui/icons-material/CreditCard'

import { theme } from '../../themes/theme'
import { openFoiBillInNewWindow, openFoiLetterInNewWindow } from '../../util/openInNewWindow'
import { WhiteBGContainer } from '../styles/Containers'
import { BoldBodyText, MedBodyText } from '../styles/Text'
import AssignedUserChip from '../common/AssignedUserChip'
import TaskName, { formatTaskTitle } from './TaskName'

export const taskTableColumns = `
  16px
  max-content
  18px
  max-content
  84px
  10px
  max-content
  minmax(0, 1fr)
  max-content
  16px
`

const Row = styled(WhiteBGContainer)`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
  align-items: center;
  height: 80px;
  padding: 0;
  border: none;
  text-align: left;
  font: inherit;
  color: inherit;
  cursor: pointer;

  :hover {
    background: ${theme.colors.primaryLight};
  }
`

const AssignedCell = styled.div`
  grid-column: 2;
`

const TaskCell = styled.div`
  grid-column: 4;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: ${theme.colors.text.body};

  svg {
    flex-shrink: 0;
    height: 22px;
    width: 22px;
  }
`

const TaskTitle = styled.div`
  max-width: 250px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const Divider = styled.div`
  grid-column: 5;
  height: 60%;
  padding-left: 40px;
`

const DividerLine = styled.div`
  width: 1px;
  height: 100%;
  background: ${theme.colors.primary};
  opacity: 0.35;
`

const FoiId = styled(BoldBodyText)`
  grid-column: 7;
  font-weight: 700;
  color: ${theme.colors.text.body};
`

const DescriptionCell = styled.div`
  grid-column: 8;
  display: flex;
  justify-content: center;
  min-width: 0;
`

const Description = styled(MedBodyText)`
  width: min(100%, 250px);
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

const DueDate = styled(MedBodyText)`
  grid-column: 9;
  justify-self: end;
  white-space: nowrap;
  transform: translateX(-10px);
`

function TaskRow({ task }) {
  const { id, type, letter, bill, assignedTo, foi } = task
  const description = foi.requestDescription || ''

  function handleOpenTask() {
    if (type === 'LETTER') openFoiLetterInNewWindow(foi.id, id)
    if (type === 'BILL') openFoiBillInNewWindow(foi.id, id)
  }

  return (
    <Row as="button" type="button" data-testid="task-row" aria-label={formatTaskTitle(task)} onClick={handleOpenTask}>
      <AssignedCell>
        {assignedTo && <AssignedUserChip firstName={assignedTo.firstName} lastName={assignedTo.lastName} color={assignedTo.chipColor} isShort />}
      </AssignedCell>
      <TaskCell>
        {type === 'LETTER' ? <MailIcon /> : <CreditCardIcon />}
        <MuiTooltip title={formatTaskTitle(task)} placement="bottom" followCursor>
          <TaskTitle>
            <TaskName type={type} letter={letter} bill={bill} />
          </TaskTitle>
        </MuiTooltip>
      </TaskCell>
      <Divider>
        <DividerLine />
      </Divider>
      <FoiId>{foi.secondaryId}</FoiId>
      <DescriptionCell>
        {description && (
          <MuiTooltip title={description} placement="bottom" followCursor>
            <Description>{description}</Description>
          </MuiTooltip>
        )}
      </DescriptionCell>
      <DueDate>{foi.dueDate ? format(foi.dueDate, 'MMM-dd-yyyy') : ''}</DueDate>
    </Row>
  )
}

TaskRow.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['LETTER', 'BILL']).isRequired,
    letter: PropTypes.shape({
      name: PropTypes.string,
      status: PropTypes.string
    }),
    bill: PropTypes.shape({
      secondaryId: PropTypes.string,
      billType: PropTypes.string,
      status: PropTypes.string
    }),
    assignedTo: PropTypes.shape({
      id: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      chipColor: PropTypes.string
    }),
    foi: PropTypes.shape({
      id: PropTypes.string.isRequired,
      secondaryId: PropTypes.string.isRequired,
      requestDescription: PropTypes.string,
      dueDate: PropTypes.instanceOf(Date)
    }).isRequired
  }).isRequired
}

export default TaskRow
